import { useTranslation } from "react-i18next";
import { useRoleLabels } from "@/hooks/useEnumLabels";
import { parseEnum } from "@/lib/enums.util";
import { RolesEnum } from "@/constants/enums/roles.enum";
import { useState } from "react";
import { UserDto } from "@/types/api/api-types";
import { createColumnHelper } from "@tanstack/react-table";
import { useCreateUser, useGetUsers, useUpdateUser, useUpdateUserPassword } from "@/api/user.api";
import { Pen, Plus, Trash2 } from "lucide-react";
import StyledTable from "@/components/styled/StyledTable";
import { UserCreateModal } from "@/components/shared/user/UserCreateModal";
import { UserUpdateModal } from "@/components/shared/user/UserUpdateModal";
import { DeleteUserModal } from "./deleteUserModal";
import { Button } from "@/components/ui/button";

const columnHelper = createColumnHelper<UserDto>();

export const GeneralInfoUsers = () => {
  const { t } = useTranslation("boClients");
  const roleLabels = useRoleLabels();
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null);
  const [editing, setEditing] = useState(0);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const { data: users } = useGetUsers();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const updatePasswordMutation = useUpdateUserPassword();

  const handleCreateUser = () => {
    setEditing(1);
    setCurrentUser(null);
  };

  const handleEdit = (user: UserDto) => {
    setCurrentUser(user);
    setEditing(2);
  };

  const columns = [
    columnHelper.display({
      id: "user",
      header: t("profileGeneralInfoUsers.columns.user"),
      meta: "text-left",
      enableSorting: false,
      cell: (info) =>
        t("profileGeneralInfoUsers.userFullName", {
          firstName: info.row.original.firstName,
          lastName: info.row.original.lastName,
        }),
    }),
    columnHelper.display({
      id: "email",
      header: t("profileGeneralInfoUsers.columns.email"),
      meta: "text-left",
      cell: (info) => info.row.original.email,
    }),
    columnHelper.display({
      id: "role",
      header: t("profileGeneralInfoUsers.columns.role"),
      meta: "text-left",
      cell: (info) => {
        const role = parseEnum(RolesEnum, info.row.original.role);
        return (
          <div className="w-fit rounded-2xl border border-gray-200 bg-gray-50 px-3 py-1">
            <p className="text-sm font-medium text-gray-700">
              {role ? roleLabels[role] : info.row.original.role}
            </p>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "details",
      header: "",
      cell: (data) => (
        <div className="flex w-full justify-center gap-1">
          {data.row.original.role !== "SUPERADMIN" && (
            <button
              onClick={() => setUserToDelete(data.row.original.id)}
              className="rounded-full p-2 hover:bg-gray-200"
            >
              <Trash2 className="text-gray-600" />
            </button>
          )}
          <div className="flex w-full flex-row justify-end">
            <button
              className="w-fit rounded-full p-2 hover:bg-gray-200"
              onClick={() => handleEdit(data.row.original)}
            >
              <Pen className="text-gray-600" />
            </button>
          </div>
        </div>
      ),
    }),
  ];

  return (
    <div className="flex w-full flex-1 flex-col gap-6 md:px-8">
      <div className="flex flex-row justify-between gap-2 border-0 border-b border-gray-200 pb-5">
        <div className="items-left flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("profileGeneralInfoUsers.title")}</h2>
          <p className="text-sm text-gray-600">{t("profileGeneralInfoUsers.subtitle")}</p>
        </div>
        <Button onClick={handleCreateUser} variant="outline">
          <Plus className="h-4 w-4" />
          {t("profileGeneralInfoUsers.addUser")}
        </Button>
      </div>
      <div className="flex flex-col gap-10">
        <StyledTable data={users || []} columns={columns} />
      </div>
      <UserCreateModal
        open={editing === 1}
        onClose={() => setEditing(0)}
        createUserMutation={createUserMutation}
      />
      {currentUser && (
        <UserUpdateModal
          open={editing === 2}
          onClose={() => setEditing(0)}
          user={currentUser}
          updateUserMutation={updateUserMutation}
          updatePasswordMutation={updatePasswordMutation}
        />
      )}
      {userToDelete && (
        <DeleteUserModal
          open={!!userToDelete}
          userId={userToDelete}
          onClose={() => setUserToDelete(null)}
        />
      )}
    </div>
  );
};
